import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader, Row, Col, Container, Spinner } from "reactstrap";
import Header from "./Header";
import SenderDetails from "./SenderDetails";
import TravelerDetails from "./TravelerDetails";
import userServices from "services/userServices";
import { useNotification } from "providers/NotificationProvider";
import UserCard from "../../components/UserCard";
import OfferCard from "../../components/OfferCard";
import { useTranslation } from "context/TranslationProvider";
import OrderTrackingCard from "modals/OrderTrackingCard";
import OrderEventsTable from "./OrderEventsTable";
import PaymentDetailsCard from "./PaymentDetailsCard";

import PickupConfirmationModal from "modals/PickupConfirmationModal";

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showPickupModal, setShowPickupModal] = useState(false);

  const fetchOrder = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const res = await userServices.fetchOrderDetails(orderId);
      setOrderDetails(res.data || res);
    } catch (error) {
      console.error("Failed to fetch order details:", error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) fetchOrder(true);
    // eslint-disable-next-line
  }, [orderId]);
  
  const handleOrderStatusSelect = (order, status) => {
    if (status === "Picked Up") {
      setShowPickupModal(true);
    } else {
      handleOrderStatusChange(order, status);
    }
  };

  const handleConfirmPickupSubmit = async ({ expectedArrivalDate, expectedArrivalTime }) => {
    if (!orderDetails?._id) return;
    try {
      setIsUpdating(true);
      const result = await userServices.confirmPickup(orderDetails._id, {
        expectedArrivalDate,
        expectedArrivalTime,
      });
      if (result?.success || result?.data) {
        notify("success", t("page.orders.toast.pickupConfirmed", "Pickup confirmed successfully."));
        setShowPickupModal(false);
        await fetchOrder(false);
      } else {
        const msg = result?.message || result?.error || "Failed to confirm pickup";
        notify("error", t(result?.messageKey || "page.orders.toast.pickupFailed", msg));
        throw new Error(msg);
      }
    } catch (error) {
      const errPayload = error?.response?.data || {};
      const msg = errPayload?.message || errPayload?.error || error?.message || "Failed to confirm pickup";
      notify("error", t(errPayload?.messageKey || "page.orders.toast.pickupFailed", msg));
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOrderStatusChange = async (order, status) => {
    try {
        setIsUpdating(true);
        const result = await userServices.updateOrderStatus(order._id, status);
        const payload = result?.data || result;

        if (payload?.success) {
          notify("success", t(payload?.messageKey || "page.orders.toast.statusUpdated", payload?.message || t("page.orders.toast.statusUpdated", "Order status updated successfully.")));
          await fetchOrder(false);
        } else {
          const errMsgKey = payload?.messageKey || payload?.error || "page.orders.toast.statusUpdateFailed";
          const msg = errMsgKey === "auth.errors.invalidOrderStatusTransition" && payload?.message 
            ? payload.message 
            : t(errMsgKey, payload?.message || t("page.orders.toast.statusUpdateFailed", "Failed to update status."));
          notify("error", msg);
        }
    } catch (error) {
      const errPayload = error?.response?.data || {};
      const errMsgKey = errPayload?.messageKey || errPayload?.error || "page.orders.toast.genericUpdateError";
      const msg = errMsgKey === "auth.errors.invalidOrderStatusTransition" && errPayload?.message 
        ? errPayload.message 
        : t(errMsgKey, errPayload?.message || t("page.orders.toast.genericUpdateError", "An error occurred while updating status."));
      notify("error", msg);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="center-loader">
        <div className="center-loader-content">
          <Spinner/>
          <p className="mt-3 text-muted">
            {t("common.loading", "Loading....")}
          </p>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="text-center mt-5">
        <p>{t("page.orders.noOrderDetails", "No order details found.")}</p>
      </div>
    );
  }

  const travelerDetails = orderDetails.traveler_listing_id || {};
  const senderDetails = orderDetails.sender_listing_id || {};
  const offer = orderDetails.acceptedOffer || {};
  const orderEvents = orderDetails?.orderEvents || [];

  return (
    <Container fluid className="user-details-wrapper mt-4">
      <Row>
        <Col lg="8">
          <Card className="shadow border-0 mb-3">
            <CardHeader className="bg-transparent">
              <Header
                orderDetails={orderDetails}
                handleOrderStatusChange={handleOrderStatusSelect}
                goBack={() => navigate(-1)}
                isUpdating={isUpdating}
              />
            </CardHeader>
          </Card>

          <Card className="shadow border-0 mb-3">
            <CardBody>
              <TravelerDetails
                travelerDetails={travelerDetails}
                offerDetails={orderDetails?.offerDetails || orderDetails?.acceptedOffer?.offerDetails}
                fallbackPickup={senderDetails?.pickUpDetails}
                expectedArrivalAt={orderDetails?.expectedArrivalAt}
                expectedArrivalTimezone={orderDetails?.expectedArrivalTimezone}
                orderDetails={orderDetails}
              />
            </CardBody>
          </Card>

          <Card className="shadow border-0 mb-3">
            <CardBody>
              <SenderDetails
                senderDetails={senderDetails}
                offerDetails={orderDetails?.offerDetails || orderDetails?.acceptedOffer?.offerDetails}
                fallbackPickup={travelerDetails?.pickUpDetails}
              />
            </CardBody>
          </Card>

          <OrderEventsTable orderEvents={orderEvents} />
        </Col>

        <Col lg="4">
          <UserCard
            user={senderDetails?.userId}
            title={t("page.orders.senderProfile", "Sender Profile")}
          />

          <UserCard
            user={travelerDetails?.userId}
            title={t("page.orders.travelerProfile", "Traveler Profile")}
          />

          <OfferCard offer={offer} orderDetails={orderDetails} />

          <OrderTrackingCard
            orderDetail={orderDetails}
            orderEvents={orderEvents}
          />
                    
          <PaymentDetailsCard orderDetails={orderDetails} />
        </Col>
      </Row>
      <PickupConfirmationModal
        open={showPickupModal}
        order={orderDetails}
        destinationTimezone={travelerDetails?.toPlace?.name || senderDetails?.toPlace?.name || ""}
        onClose={() => setShowPickupModal(false)}
        onConfirm={handleConfirmPickupSubmit}
      />
    </Container>
  );
};

export default OrderDetails;
